// Report a disciplinary incident: the cockpit version of the Disciplinary
// Incident Typeform (MIJ5oauG on fleetly.co.za/discipline), reported against
// the Code of Conduct section 6 of the v6 agreement. db/migrations/4400 and 4401,
// docs/DISCIPLINARY-INCIDENTS.md.
//
// What changed from the Typeform, and why:
// - The reporter is the person signed in. "Select your name" let anyone file as
//   anyone, and the record has to carry who said it.
// - The nine Typeform categories became the Code's own offences, because a
//   sanction can only be read from an offence the agreement names.
// - "Nature of the disciplinary action" became "what you think should happen
//   next". Issuing a warning, a hearing notice or a suspension is the manager's
//   step after release, never the reporter's, so the form asks for an opinion
//   and the manager decides.
// - The six address boxes became one line, since most incidents happen at a
//   handover point, on the road or in a message.
//
// The form decides nothing. Reporting puts nothing on the driver's record and
// tells him nothing; every refusal is Postgres's sentence, shown as it comes.
import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Paperclip, ShieldAlert, X } from 'lucide-react'
import {
  useGetDisciplinaryIncidentOptions, useReportDisciplinaryIncident, useSignDisciplinaryIncidentUpload,
} from '../../hooks/backend/disciplinary'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../lib/shadcn/dialog'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { uploadSigned, type Signed } from '../../utils/signedUpload'
import { todaySast } from '../../utils/disciplinary'
import {
  checkEvidence, EVIDENCE_ACCEPT, stepTone, type IncidentOptions, type Offence, type Suggestion,
} from '../../utils/disciplinaryIncidents'

type Props = {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** Fixed when opened from a driver record; chosen on the form otherwise. */
  driverId?: string | null
  driverName?: string | null
  onReported?: () => void
}

const blank = () => ({
  driver_id: '', vehicle_id: '', occurred_on: todaySast(), place: '', description: '',
  witnesses: '', immediate_action: '', requested_action: 'record',
})

const GROUPS: { title: string; hint: string; pick: (o: Offence) => boolean }[] = [
  { title: 'Serious offences', hint: 'Dismissal is available on a first occasion, and only a hearing can decide it.', pick: (o) => o.kind === 'serious' },
  { title: 'Progressive offences', hint: 'Each one moves a step up the ladder while an earlier warning is in force.', pick: (o) => o.kind === 'progressive' && o.in_code_table },
  { title: 'Other misconduct under clause 27.2', hint: 'Not in the Code\'s table: the general ladder of clause 27.1 applies.', pick: (o) => !o.in_code_table },
]

export function IncidentReportDialog({ open, onOpenChange, driverId, driverName, onReported }: Props) {
  const opts = useGetDisciplinaryIncidentOptions<IncidentOptions>()
  const sign = useSignDisciplinaryIncidentUpload<Signed>()
  const report = useReportDisciplinaryIncident<{ ok: boolean; id: string; ref: string; suggestion: Suggestion }>()
  const [f, setF] = useState(blank)
  const [offences, setOffences] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ ref: string; suggestion: Suggestion } | null>(null)
  const loadOpts = opts.trigger

  useEffect(() => {
    if (!open) return
    setF({ ...blank(), driver_id: driverId ?? '' })
    setOffences([])
    setFiles([])
    setDone(null)
    void loadOpts({}, { skipCache: true }).catch(() => undefined)
  }, [open, driverId, loadOpts])

  const o = opts.data
  // The car defaults to the one the driver is on today; the reporter may change it.
  useEffect(() => {
    if (!o || !f.driver_id || f.vehicle_id) return
    const d = o.drivers.find((x) => x.id === f.driver_id)
    if (d?.vehicle_id) setF((x) => ({ ...x, vehicle_id: d.vehicle_id ?? '' }))
  }, [o, f.driver_id, f.vehicle_id])

  const set = (k: keyof ReturnType<typeof blank>) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  const fileProblems = useMemo(() => files.map(checkEvidence).filter((x): x is string => !!x), [files])
  const ready = !!f.driver_id && !!f.occurred_on && f.place.trim() !== '' && offences.length > 0 &&
    f.description.trim().length >= 30 && f.witnesses.trim() !== '' && f.immediate_action.trim() !== '' &&
    fileProblems.length === 0 && offences.length <= 5
  const chosen = (o?.offences ?? []).filter((x) => offences.includes(x.key))
  const name = driverName ?? o?.drivers.find((d) => d.id === f.driver_id)?.name ?? 'the driver'

  function toggle(key: string, on: boolean) {
    setOffences((xs) => (on ? [...new Set([...xs, key])] : xs.filter((x) => x !== key)))
  }

  async function submit() {
    setBusy(true)
    try {
      const evidence: { path: string; filename: string }[] = []
      for (const file of files) {
        const path = await uploadSigned(await sign.trigger({ driver_id: f.driver_id, filename: file.name }, { skipCache: true }), file)
        evidence.push({ path, filename: file.name })
      }
      const r = await report.trigger({ ...f, vehicle_id: f.vehicle_id || null, offence_keys: offences, evidence }).result
      setDone({ ref: r.ref, suggestion: r.suggestion })
      toast.success(`${r.ref} reported. ${name}'s manager has a task to release or close it.`)
      onReported?.()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v) }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report a disciplinary incident</DialogTitle>
          <DialogDescription>
            Facts only. Reporting puts nothing on {name === 'the driver' ? 'the driver\'s' : `${name}'s`} record and tells {name === 'the driver' ? 'him' : name} nothing:
            the manager reads it, sees the step the agreement suggests, and releases it as a written allegation or closes it.
          </DialogDescription>
        </DialogHeader>

        {opts.error && !o ? (
          <p className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {opts.error}
          </p>
        ) : !o ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the form…</p>
        ) : done ? (
          <ReportedPanel refText={done.ref} s={done.suggestion} />
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Reported by <span className="font-medium text-foreground">{o.reporter ?? 'you'}</span>, today.</p>

            {!driverId && (
              <div className="space-y-1">
                <Label htmlFor="di-driver">Who is it about?</Label>
                <NativeSelect id="di-driver" value={f.driver_id} onChange={(e) => setF((x) => ({ ...x, driver_id: e.target.value, vehicle_id: '' }))}>
                  <option value="">Choose the driver</option>
                  {o.drivers.map((d) => <option key={d.id} value={d.id}>{d.name ?? 'Unnamed'}{d.status !== 'active' ? ` (${d.status})` : ''}</option>)}
                </NativeSelect>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="di-date">What day did it happen?</Label>
                <Input id="di-date" type="date" max={todaySast()} value={f.occurred_on} onChange={set('occurred_on')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="di-car">Which car? (optional)</Label>
                <NativeSelect id="di-car" value={f.vehicle_id} onChange={set('vehicle_id')}>
                  <option value="">No car involved</option>
                  {o.vehicles.map((v) => <option key={v.id} value={v.id}>{v.label ?? v.plate}</option>)}
                </NativeSelect>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="di-place">Where did it happen?</Label>
              <Input id="di-place" value={f.place} onChange={set('place')} maxLength={300}
                placeholder="For example: the handover at Sandton Gautrain, or over WhatsApp" />
            </div>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">What did he do? Choose what the facts show, up to five.</legend>
              {GROUPS.map((g) => {
                const rows = o.offences.filter(g.pick)
                if (rows.length === 0) return null
                return (
                  <div key={g.title} className="space-y-1.5 rounded-md border border-border p-2.5">
                    <p className="text-xs font-semibold text-foreground">{g.title}</p>
                    <p className="text-[11px] text-muted-foreground">{g.hint}</p>
                    <div className="grid grid-cols-1 gap-1.5 pt-1 sm:grid-cols-2">
                      {rows.map((x) => (
                        <label key={x.key} className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted/60">
                          <Checkbox className="mt-0.5" checked={offences.includes(x.key)} onCheckedChange={(v) => toggle(x.key, v === true)} />
                          <span>
                            {x.offence}
                            <span className="ml-1 text-[11px] text-muted-foreground">Code {x.code_ref}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
              {chosen.some((x) => x.detected) && (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                  The nightly checks already raise {chosen.filter((x) => x.detected).map((x) => x.offence.split(':')[0].toLowerCase()).join(' and ')} from the telematics,
                  the service bookings and the Late Shift Submissions. Report it here only if it is not already on the Disciplinary page.
                </p>
              )}
              {chosen.filter((x) => x.note).map((x) => (
                <p key={x.key} className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{x.offence}: </span>{x.note}</p>
              ))}
              {offences.length > 5 && <p className="text-xs text-rose-700 dark:text-rose-300">Choose at most five.</p>}
            </fieldset>

            <div className="space-y-1">
              <Label htmlFor="di-desc">What happened?</Label>
              <Textarea id="di-desc" rows={5} value={f.description} onChange={set('description')} maxLength={4000}
                placeholder="Facts only: what happened, at what time, what was said, who saw it. He will read this as the allegation." />
              {f.description.trim().length > 0 && f.description.trim().length < 30 && (
                <p className="text-xs text-muted-foreground">A few sentences, please: the manager and the driver both read this.</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="di-files">Evidence (optional)</Label>
              <Input id="di-files" type="file" multiple accept={EVIDENCE_ACCEPT}
                onChange={(e) => { const add = Array.from(e.target.files ?? []); setFiles((xs) => [...xs, ...add].slice(0, 10)); e.target.value = '' }} />
              <p className="text-xs text-muted-foreground">Photos, dashcam or phone video, a voice note, screenshots, telematics or a PDF. Up to ten files, 25 MB each.</p>
              {files.length > 0 && (
                <ul className="space-y-1">
                  {files.map((file, n) => (
                    <li key={`${file.name}-${n}`} className="flex items-center gap-2 text-xs">
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate">{file.name}</span>
                      <button type="button" aria-label={`Remove ${file.name}`} className="text-muted-foreground hover:text-foreground"
                        onClick={() => setFiles((xs) => xs.filter((_, i) => i !== n))}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {fileProblems.map((p) => <p key={p} className="text-xs text-rose-700 dark:text-rose-300">{p}</p>)}
            </div>

            <div className="space-y-1">
              <Label htmlFor="di-wit">Witnesses and anyone else involved</Label>
              <Textarea id="di-wit" rows={2} value={f.witnesses} onChange={set('witnesses')} maxLength={1500}
                placeholder="Full name and cellphone number, separated by commas. Write None if nobody else saw it." />
            </div>

            <div className="space-y-1">
              <Label htmlFor="di-act">What was done straight afterwards?</Label>
              <Textarea id="di-act" rows={2} value={f.immediate_action} onChange={set('immediate_action')} maxLength={1500}
                placeholder="For example: took the keys, called the partner, sent him home. Write Nothing yet if nothing was." />
            </div>

            <div className="space-y-1">
              <Label htmlFor="di-next">What do you think should happen next?</Label>
              <NativeSelect id="di-next" value={f.requested_action} onChange={set('requested_action')}>
                {o.actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </NativeSelect>
              <p className="text-xs text-muted-foreground">
                This is your view for the manager. The step itself is the manager's after hearing him, and anything above a verbal warning is decided at a hearing.
                {f.requested_action === 'suspend_and_hearing' ? ' A suspension is on full pay, and the hearing must be held within ten working days or he returns to the roster (clause 27.5).' : ''}
              </p>
            </div>

            <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Never report a grievance, a true statement, a referral to the CCMA or union activity as misconduct. Clause 27.8 protects all of them, however strongly they were put.
            </p>
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button variant="brand" disabled={!ready || busy || !o} onClick={() => void submit()}>
                {busy && <Spinner className="mr-1 h-4 w-4" />} {files.length > 0 ? 'Upload and report it' : 'Report it'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReportedPanel({ refText, s }: { refText: string; s: Suggestion | null | undefined }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground">
        <span className="font-semibold">{refText}</span> is reported. His manager has a task to read it and release it as a written allegation, or close it.
      </p>
      {s && <div className="space-y-1.5 rounded-md border border-border bg-muted/40 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">The agreement suggests</span>
          <Chip tone={s.applies ? stepTone(s.step) : 'neutral'}>{s.applies ? s.step_label : 'Coaching only'}</Chip>
        </div>
        {s.explanation && <p className="text-xs text-foreground">{s.explanation}</p>}
      </div>}
    </div>
  )
}
